/**
 * @module index
 * @package @geenius/adapters/solidjs-css
 * @description Implements the SolidJS CSS adapter provider and context access
 * helpers. This module keeps the SolidJS CSS runtime self-contained while
 * relying only on the shared framework-agnostic adapter contract.
 */

import type {
  AdapterDomain,
  AdapterStatusInfo,
  AdminAdapter,
  AiAdapter,
  AuthAdapter,
  DbAdapter,
  FileStorageAdapter,
  LoggerAdapter,
  PaymentsAdapter,
} from "@geenius/adapters";
import {
  ADAPTER_DOMAINS,
  AdapterContextError,
  AdapterError,
  createConsoleLoggerAdapter,
  createLocalStorageAdminAdapter,
  createLocalStorageAiAdapter,
  createLocalStorageAuthAdapter,
  createLocalStorageDbAdapter,
  createLocalStorageFileAdapter,
  createLocalStoragePaymentsAdapter,
  createNoopPaymentsAdapter,
  DOMAIN_LABELS,
  getDomainConfig,
  isAdaptersConfigured,
} from "@geenius/adapters";
import type { Context } from "solid-js";
import {
  createContext,
  createSignal,
  onCleanup,
  onMount,
  useContext,
} from "solid-js";
import { createStore } from "solid-js/store";

/**
 * Map from adapter domain identifier to its concrete adapter interface.
 *
 * Used as the canonical typing for the SolidJS CSS adapter registry; each
 * key corresponds to a supported domain and its value is the contract every
 * provider implementation for that domain must satisfy.
 */
export interface AdapterDomainMap {
  db: DbAdapter;
  auth: AuthAdapter;
  payments: PaymentsAdapter;
  ai: AiAdapter;
  storage: FileStorageAdapter;
  admin: AdminAdapter;
  logger: LoggerAdapter;
}

/**
 * Union of supported adapter domain identifiers derived from {@link AdapterDomainMap}.
 */
export type AdapterDomainKey = keyof AdapterDomainMap;

/**
 * SolidJS CSS adapter bag keyed by domain.
 *
 * @property db Database adapter implementation when configured.
 * @property auth Authentication adapter implementation when configured.
 * @property payments Payments adapter implementation when configured.
 * @property ai AI adapter implementation when configured.
 * @property storage File-storage adapter implementation when configured.
 * @property admin Admin adapter implementation when configured.
 * @property logger Logger adapter implementation when configured.
 */
export interface AdapterSet extends Partial<AdapterDomainMap> {
  fileStorage?: FileStorageAdapter;
}

/** Registry shape accepted by the public SolidJS CSS provider. */
export type AdapterRegistry = AdapterSet;

/**
 * SolidJS CSS adapter context value exposed to primitives and provider consumers.
 *
 * @property adapters Adapter bag passed into the provider.
 * @property statuses Current status record for each adapter domain.
 * @property getAdapter Returns a configured adapter for a specific domain or throws when missing.
 * @property isReady Reports whether a domain is configured and connected.
 * @property isLoading Indicates whether initial health checks are still running.
 */
export interface AdapterContextValue {
  adapters: AdapterSet;
  statuses: Record<AdapterDomain, AdapterStatusInfo>;
  getAdapter: <K extends AdapterDomainKey>(domain: K) => AdapterDomainMap[K];
  isReady: (domain: AdapterDomain) => boolean;
  isLoading: boolean;
}

const AdapterContext: Context<AdapterContextValue | undefined> =
  createContext<AdapterContextValue>();
type ProviderChildren = Parameters<
  typeof AdapterContext.Provider
>[0]["children"];
type ProviderElement = import("solid-js").JSX.Element;

/**
 * Map of domain-specific health probes supplied by the host app.
 *
 * When a domain has a custom probe, the provider calls it instead of the
 * built-in probe during the health-check lifecycle. The probe receives the
 * adapter instance for its domain and the full adapter bag.
 */
export type AdapterHealthProbeMap = Partial<{
  [D in AdapterDomainKey]: (
    adapter: AdapterDomainMap[D],
    adapters: AdapterSet,
  ) => Promise<void> | void;
}>;

/**
 * Props accepted by the SolidJS CSS adapter provider.
 *
 * @property adapters Optional adapter bag made available to descendant primitives and components.
 * @property registry Optional registry alias for adapter overrides.
 * @property db Optional direct database adapter override.
 * @property auth Optional direct authentication adapter override.
 * @property payments Optional direct payments adapter override.
 * @property ai Optional direct AI adapter override.
 * @property storage Optional direct file-storage adapter override.
 * @property fileStorage Optional slot-API alias for the file-storage adapter.
 * @property admin Optional direct admin adapter override.
 * @property logger Optional direct logger adapter override.
 * @property children Solid subtree that should receive adapter context.
 * @property healthCheck Whether the provider should probe configured adapters on mount.
 * @property healthProbes Optional domain-specific health probes supplied by the host app.
 * @property statusOverrides Optional deterministic status overrides for tests and previews.
 * @property onError Optional typed error callback invoked when provider lifecycle checks fail.
 */
export interface AdapterProviderProps extends Partial<AdapterDomainMap> {
  adapters?: AdapterSet;
  registry?: Partial<AdapterRegistry>;
  fileStorage?: FileStorageAdapter;
  children: ProviderChildren;
  healthCheck?: boolean;
  healthProbes?: AdapterHealthProbeMap;
  statusOverrides?: Partial<Record<AdapterDomain, Partial<AdapterStatusInfo>>>;
  onError?: (error: AdapterError) => void;
}

/**
 * Plural alias for {@link AdapterProviderProps}; preserved for naming parity
 * with sibling framework cells that historically exported the plural form.
 */
export type AdaptersProviderProps = AdapterProviderProps;

interface ResolvedAdapterSet {
  adapters: AdapterSet;
  fallbackDomains: ReadonlySet<AdapterDomainKey>;
}

const ADAPTER_DOMAIN_KEYS = ADAPTER_DOMAINS as readonly AdapterDomainKey[];

const DEFAULT_PROVIDER_BY_DOMAIN: Record<AdapterDomainKey, string> = {
  db: "localStorage",
  auth: "localStorage",
  payments: "localStorage",
  ai: "localStorage",
  storage: "localStorage",
  admin: "localStorage",
  logger: "console",
};

function createDefaultAdapters(): AdapterDomainMap {
  return {
    db: createLocalStorageDbAdapter(),
    auth: createLocalStorageAuthAdapter(),
    payments: createDefaultPaymentsAdapter(),
    ai: createLocalStorageAiAdapter(),
    storage: createLocalStorageFileAdapter(),
    admin: createLocalStorageAdminAdapter(),
    logger: createConsoleLoggerAdapter(),
  };
}

function createDefaultPaymentsAdapter(): PaymentsAdapter {
  try {
    return createLocalStoragePaymentsAdapter();
  } catch {
    return createNoopPaymentsAdapter();
  }
}

function setResolvedAdapter(
  adapters: AdapterSet,
  fallbackDomains: Set<AdapterDomainKey>,
  domain: AdapterDomainKey,
  adapter: AdapterDomainMap[AdapterDomainKey] | undefined,
): void {
  if (adapter === undefined) {
    return;
  }

  const writableAdapters = adapters as Partial<
    Record<AdapterDomainKey, AdapterDomainMap[AdapterDomainKey]>
  >;
  writableAdapters[domain] = adapter;
  fallbackDomains.delete(domain);
}

function resolveAdapterSet(props: AdapterProviderProps): ResolvedAdapterSet {
  const defaults = createDefaultAdapters();
  const fallbackDomains = new Set<AdapterDomainKey>(ADAPTER_DOMAIN_KEYS);
  const adapters: AdapterSet = {
    ...defaults,
    fileStorage: defaults.storage,
  };

  const applyAdapterBag = (bag: AdapterSet | undefined): void => {
    if (bag === undefined) {
      return;
    }

    for (const domain of ADAPTER_DOMAIN_KEYS) {
      setResolvedAdapter(adapters, fallbackDomains, domain, bag[domain]);
    }

    setResolvedAdapter(adapters, fallbackDomains, "storage", bag.fileStorage);
  };

  applyAdapterBag(props.registry);
  applyAdapterBag(props.adapters);
  setResolvedAdapter(adapters, fallbackDomains, "db", props.db);
  setResolvedAdapter(adapters, fallbackDomains, "auth", props.auth);
  setResolvedAdapter(adapters, fallbackDomains, "payments", props.payments);
  setResolvedAdapter(adapters, fallbackDomains, "ai", props.ai);
  setResolvedAdapter(adapters, fallbackDomains, "storage", props.storage);
  setResolvedAdapter(adapters, fallbackDomains, "storage", props.fileStorage);
  setResolvedAdapter(adapters, fallbackDomains, "admin", props.admin);
  setResolvedAdapter(adapters, fallbackDomains, "logger", props.logger);

  adapters.fileStorage = adapters.storage;
  return { adapters, fallbackDomains };
}

function getInitialProviderName(
  domain: AdapterDomainKey,
  adapters: AdapterSet,
  fallbackDomains: ReadonlySet<AdapterDomainKey>,
  statusOverrides?: Partial<Record<AdapterDomain, Partial<AdapterStatusInfo>>>,
): string {
  const overrideProvider = statusOverrides?.[domain]?.provider;
  if (overrideProvider !== undefined) {
    return overrideProvider;
  }

  if (!adapters[domain]) {
    return "none";
  }

  if (fallbackDomains.has(domain)) {
    return DEFAULT_PROVIDER_BY_DOMAIN[domain];
  }

  if (!isAdaptersConfigured()) {
    return "configured";
  }

  return getDomainConfig(domain)?.provider ?? "configured";
}

function createInitialStatus(
  domain: AdapterDomainKey,
  adapters: AdapterSet,
  fallbackDomains: ReadonlySet<AdapterDomainKey>,
  healthCheck: boolean,
  statusOverrides?: Partial<Record<AdapterDomain, Partial<AdapterStatusInfo>>>,
): AdapterStatusInfo {
  const adapter = adapters[domain];
  return {
    domain,
    provider: getInitialProviderName(
      domain,
      adapters,
      fallbackDomains,
      statusOverrides,
    ),
    status: adapter
      ? healthCheck
        ? "initializing"
        : "connected"
      : "disconnected",
    lastCheckedAt: Date.now(),
    ...statusOverrides?.[domain],
  };
}

function toAdapterError(domain: AdapterDomain, error: unknown): AdapterError {
  if (error instanceof AdapterError) {
    return error;
  }

  const message =
    error instanceof Error ? error.message : "Health check failed";
  return new AdapterError(message, domain, "SERVICE_ERROR", error);
}

async function probeAdapterDomain(
  domain: AdapterDomainKey,
  adapters: AdapterSet,
  healthProbes?: AdapterHealthProbeMap,
): Promise<{ connected: true } | { connected: false; error: string }> {
  const customProbe = healthProbes?.[domain];
  if (customProbe) {
    await customProbe(adapters[domain] as never, adapters);
    return { connected: true };
  }

  switch (domain) {
    case "db":
      await adapters.db?.count("__geenius_adapter_healthcheck");
      return adapters.db
        ? { connected: true }
        : { connected: false, error: "DB adapter is not configured." };
    case "auth":
      await adapters.auth?.getSession();
      return adapters.auth
        ? { connected: true }
        : { connected: false, error: "Auth adapter is not configured." };
    case "payments":
      await adapters.payments?.getPlans();
      return adapters.payments
        ? { connected: true }
        : { connected: false, error: "Payments adapter is not configured." };
    case "storage":
      await adapters.storage?.list("__geenius_adapter_healthcheck");
      return adapters.storage
        ? { connected: true }
        : { connected: false, error: "Storage adapter is not configured." };
    case "admin":
      await adapters.admin?.getMetrics();
      return adapters.admin
        ? { connected: true }
        : { connected: false, error: "Admin adapter is not configured." };
    case "logger":
      await adapters.logger?.child({ healthCheck: true });
      return adapters.logger
        ? { connected: true }
        : { connected: false, error: "Logger adapter is not configured." };
    case "ai":
      await adapters.ai?.complete("__geenius_adapter_healthcheck", {
        maxTokens: 1,
        temperature: 0,
      });
      return adapters.ai
        ? { connected: true }
        : { connected: false, error: "AI adapter is not configured." };
  }
}

/**
 * Provides the SolidJS CSS adapter context and optional health-check lifecycle.
 *
 * @param props Provider props including the adapter bag, child tree, and optional health-check settings.
 * @returns A SolidJS context provider that exposes adapters and derived status metadata.
 */
export function AdapterProvider(props: AdapterProviderProps): ProviderElement {
  const resolved = resolveAdapterSet(props);
  const adapters = resolved.adapters;
  const healthCheckEnabled = props.healthCheck ?? false;
  const initialStatuses: Record<AdapterDomain, AdapterStatusInfo> = {
    db: createInitialStatus(
      "db",
      adapters,
      resolved.fallbackDomains,
      healthCheckEnabled,
      props.statusOverrides,
    ),
    auth: createInitialStatus(
      "auth",
      adapters,
      resolved.fallbackDomains,
      healthCheckEnabled,
      props.statusOverrides,
    ),
    payments: createInitialStatus(
      "payments",
      adapters,
      resolved.fallbackDomains,
      healthCheckEnabled,
      props.statusOverrides,
    ),
    ai: createInitialStatus(
      "ai",
      adapters,
      resolved.fallbackDomains,
      healthCheckEnabled,
      props.statusOverrides,
    ),
    storage: createInitialStatus(
      "storage",
      adapters,
      resolved.fallbackDomains,
      healthCheckEnabled,
      props.statusOverrides,
    ),
    admin: createInitialStatus(
      "admin",
      adapters,
      resolved.fallbackDomains,
      healthCheckEnabled,
      props.statusOverrides,
    ),
    logger: createInitialStatus(
      "logger",
      adapters,
      resolved.fallbackDomains,
      healthCheckEnabled,
      props.statusOverrides,
    ),
  };

  const [statuses, setStatuses] = createStore(initialStatuses);
  const [isLoading, setIsLoading] = createSignal(healthCheckEnabled);

  onMount(async () => {
    if (!props.healthCheck) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    onCleanup(() => {
      cancelled = true;
    });

    const checks = ADAPTER_DOMAIN_KEYS.map(async (domain) => {
      const adapter = adapters[domain];
      if (!adapter) {
        return;
      }

      try {
        if (cancelled) {
          return;
        }

        setStatuses(domain, "status", "initializing");

        const result = await probeAdapterDomain(
          domain,
          adapters,
          props.healthProbes,
        );
        if (cancelled) {
          return;
        }

        if (!result.connected) {
          setStatuses(domain, "status", "disconnected");
          setStatuses(domain, "error", result.error);
          setStatuses(domain, "lastCheckedAt", Date.now());
          return;
        }

        setStatuses(domain, "status", "connected");
        setStatuses(domain, "error", undefined);
        setStatuses(domain, "lastCheckedAt", Date.now());
      } catch (error) {
        const adapterError = toAdapterError(domain, error);
        props.onError?.(adapterError);

        if (cancelled) {
          return;
        }

        setStatuses(domain, "status", "error");
        setStatuses(domain, "error", adapterError.message);
        setStatuses(domain, "lastCheckedAt", Date.now());
      }
    });

    await Promise.allSettled(checks);
    if (!cancelled) {
      setIsLoading(false);
    }
  });

  const getAdapter = <K extends AdapterDomainKey>(
    domain: K,
  ): AdapterDomainMap[K] => {
    const adapter = adapters[domain] as AdapterDomainMap[K] | undefined;
    if (adapter === undefined) {
      throw new AdapterContextError(
        `${DOMAIN_LABELS[domain]} adapter not configured in <AdapterProvider>`,
        "ADAPTER_NOT_CONFIGURED",
      );
    }

    return adapter;
  };

  const isReady = (domain: AdapterDomain): boolean => {
    const domainKey = domain as AdapterDomainKey;
    return !!adapters[domainKey] && statuses[domain]?.status === "connected";
  };

  const value: AdapterContextValue = {
    adapters,
    statuses,
    getAdapter,
    isReady,
    get isLoading() {
      return isLoading();
    },
  };

  return (
    <AdapterContext.Provider value={value}>
      {props.children}
    </AdapterContext.Provider>
  );
}

/**
 * Plural provider alias matching the public SolidJS CSS contract.
 */
export const AdaptersProvider = AdapterProvider;

/**
 * Reads the SolidJS CSS adapter context from the nearest provider.
 *
 * @returns The active adapter context value with adapters, statuses, and readiness helpers.
 * @throws {AdapterContextError} When called outside of an `<AdapterProvider>` tree.
 */
export function useAdapterContext(): AdapterContextValue {
  const context = useContext(AdapterContext);
  if (!context) {
    throw new AdapterContextError(
      "useAdapterContext must be used within <AdapterProvider>",
      "MISSING_CONTEXT",
    );
  }

  return context;
}
